<template>
  <div class="business-intelligence-bids-list" :class="size">
    <BIBadge
      v-for="bid in shownBids"
      type="bidder"
      :key="bid.id"
      :source="source"
      :data="bid"
      :size="size"
      :variant="getVariant(bid)"
      :displayIndex="getIndex(bid)"
      :hasWinner="hasWinner"
      @add-competitor="onAddCompetitorClicked"
      @edit-competitor="onEditCompetitorClicked"
      @add-winner="onAddWinnerClicked"
      @action-succeeded="onActionSucceeded"
    >
      <template slot="chip">
        <BaseTag v-if="getDisplayValue(bid)" preset="soft" :tone="getBiBidListTagTone(bid, rankIndex(bid), bidPool)">
          {{ getDisplayValue(bid) }}
        </BaseTag>
      </template>
    </BIBadge>
    <v-popover
      placement="top"
      trigger="hover"
      popoverClass="business-intelligence__bids-list-tooltip"
      v-if="hiddenBids.length > 0"
    >
      <span>+{{ hiddenBids.length }}</span>
      <template v-slot:popover>
        <div>
          <div class="__bid-item" v-for="(bid, index) in hiddenBids" :key="index">
            <span>{{ bid.supplier_name }}</span>
            <span class="number-monospace">{{ displayCurrency(bid.amount, 0) }}</span>
          </div>
        </div>
      </template>
    </v-popover>
  </div>
</template>
<script>
import BIBadge from './BIBadge.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import { getBiBidListTagTone } from '@/util/tag-tones/bi-bids-tag';
import { formatPercentageShort } from '@/util/number-util';
import { displayCurrency } from '@/util/currency-util';
import { rankIn, selectFeatured } from '@/util/bi-featured-entries';

export default {
  name: 'BIBidsList',
  components: { BIBadge, BaseTag },
  props: {
    size: {
      type: String,
      default: () => 'compact', // compact, full
    },
    tenderId: {
      type: String,
    },
    bids: {
      type: Array,
      required: true,
      default: () => [],
    },
    limit: {
      type: Number,
      required: false,
    },
    currentSupplier: {
      type: String,
      required: false,
      default: null,
    },
    hasWinner: {
      type: Boolean,
      required: false,
      default: false,
    },
    source: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    listLimit() {
      return this.limit ? this.limit : this.bids.length;
    },
    sortedBids() {
      return [...this.bids].sort((a, b) => {
        // display bids that don't have amounts last in the list
        // for bids with values, sort descending
        if (a.amount == null && b.amount == null) return 0;
        if (a.amount == null) return 1;
        if (b.amount == null) return -1;
        return a.amount - b.amount;
      });
    },
    officialBids() {
      return this.sortedBids ? this.sortedBids.filter((bid) => !bid.is_manual_entry) : [];
    },
    manualEntries() {
      return this.sortedBids ? this.sortedBids.filter((bid) => bid.is_manual_entry) : [];
    },
    bidPool() {
      return this.officialBids.length > 0 ? this.officialBids : this.manualEntries;
    },
    shownBids() {
      return selectFeatured(this.bidPool, this.listLimit);
    },
    hiddenBids() {
      const shown = new Set(this.shownBids);
      return this.bidPool.filter((bid) => !shown.has(bid));
    },
  },
  methods: {
    displayCurrency,
    rankIndex(bid) {
      return rankIn(this.bidPool, bid);
    },
    getIndex(bid) {
      return this.rankIndex(bid) + 1;
    },
    getVariant(bid) {
      if (bid.is_competitor) {
        return 'active';
      } else if (bid.is_tenant) {
        return 'emphasis';
      }
      return 'default';
    },
    getBiBidListTagTone,
    calculatePercentage(bid) {
      const lowestBid = this.bidPool[0];
      if (lowestBid && lowestBid.amount) return (lowestBid.amount - bid.amount) / lowestBid.amount;
      else return null;
    },
    getDisplayValue(bid) {
      if (this.rankIndex(bid) === 0) {
        return this.$t('page.business-intelligence.lowest-bid');
      }
      const percentage = this.calculatePercentage(bid);
      if (percentage) return `+${formatPercentageShort(-percentage)}`;
      else return null;
    },
    onActionSucceeded() {
      this.$emit('action-succeeded');
    },
    onAddCompetitorClicked(supplier_name) {
      this.$emit('add-competitor', supplier_name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$emit('edit-competitor', competitor, name);
    },
    onAddWinnerClicked(bid) {
      this.$emit('add-winner', this.tenderId, bid.supplier_name, bid.amount);
    },
  },
};
</script>

<style lang="scss">
.business-intelligence-bids-list {
  display: flex;
  width: 100%;
  gap: 8px;

  &.compact {
    flex-direction: row;
  }

  &.full {
    flex-direction: column;
  }
}

.business-intelligence__bids-list-tooltip {
  padding: 8px;
  font-size: $font-size-xsmall;
  background-color: $color-normal !important;
  color: $color-white !important;
  box-shadow: $shadow-tooltip;

  .popover-arrow {
    background-color: $color-normal !important;
  }

  .__bid-item {
    display: flex;
    gap: 8px;
    justify-content: space-between;

    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }
}
</style>
